import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, 
  Users, 
  UserPlus, 
  UserCheck, 
  UserX, 
  UserMinus, 
  Crown, 
  Trophy, 
  Zap,
  MessageSquare,
  X,
  Check,
  Clock,
  AlertCircle,
  Shield,
  ShieldOff,
  Eye,
  User
} from 'lucide-react';
import api from '../services/api';
import { useUser } from '../context/UserContext';

const Friends = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState('friends');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState({ sent: [], received: [] });
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [pendingRequests, setPendingRequests] = useState(new Set()); // Track locally sent requests

  useEffect(() => {
    loadFriendsData();
  }, []);

  const loadFriendsData = async () => {
    setLoading(true);
    try {
      const [friendsRes, requestsRes, blockedRes] = await Promise.all([
        api.getFriends(),
        api.getFriendRequests(),
        api.getBlockedUsers()
      ]);

      setFriends(friendsRes.data || []);
      setFriendRequests(requestsRes.data || { sent: [], received: [] });
      setBlockedUsers(blockedRes.data || []);
      
      // Update pending requests based on actual sent requests
      const sentRequestIds = new Set((requestsRes.data?.sent || []).map(req => req._id));
      setPendingRequests(sentRequestIds);
    } catch (error) {
      console.error('Error loading friends data:', error);
      setMessage('Failed to load friends data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await api.searchUsers(query);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Search error:', error);
      setMessage('Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleFriendAction = async (action, userId) => {
    setLoading(true);
    try {
      switch (action) {
        case 'send':
          await api.sendFriendRequest(userId);
          setMessage('Friend request sent!');
          setPendingRequests(prev => new Set([...prev, userId]));
          // Refresh search results if we're on the search tab
          if (activeTab === 'search' && searchQuery.length >= 2) {
            handleSearch(searchQuery);
          }
          break;
        case 'accept':
          await api.acceptFriendRequest(userId);
          setMessage('Friend request accepted!');
          break;
        case 'decline':
          await api.declineFriendRequest(userId);
          setMessage('Friend request declined');
          break;
        case 'cancel':
          await api.cancelFriendRequest(userId);
          setMessage('Friend request cancelled');
          setPendingRequests(prev => {
            const newSet = new Set(prev);
            newSet.delete(userId);
            return newSet;
          });
          break;
        case 'remove':
          if (window.confirm('Are you sure you want to remove this friend?')) {
            await api.removeFriend(userId);
            setMessage('Friend removed');
          }
          break;
        case 'block':
          if (window.confirm('Are you sure you want to block this user?')) {
            await api.blockUser(userId);
            setMessage('User blocked');
            // Refresh search results if we're on the search tab
            if (activeTab === 'search' && searchQuery.length >= 2) {
              handleSearch(searchQuery);
            }
          }
          break;
        case 'unblock':
          await api.unblockUser(userId);
          setMessage('User unblocked');
          // Refresh search results if we're on the search tab
          if (activeTab === 'search' && searchQuery.length >= 2) {
            handleSearch(searchQuery);
          }
          break;
        case 'addBack':
          await api.sendFriendRequest(userId);
          setMessage('Friend request sent back!');
          break;
        case 'chat':
          await handleStartChat(userId);
          break;
      }
      loadFriendsData();
    } catch (error) {
      console.error('Friend action error:', error);
      setMessage(error.message || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const handleStartChat = async (userId) => {
    try {
      // Get or create chat with this user
      const response = await api.getChatWithUser(userId);
      const chatId = response.data.chatId;
      
      // Navigate to the chat with this specific chat ID
      navigate(`/chat/${chatId}`);
    } catch (error) {
      console.error('Error starting chat:', error);
      setMessage('Failed to start chat. Please try again.');
    }
  };

  const getFriendshipButton = (user, status) => {
    // Check if we have a pending request for this user
    const hasPendingRequest = pendingRequests.has(user._id);
    
    const buttonBaseClasses = "flex items-center justify-center space-x-1 px-2 py-1.5 text-xs rounded transition-colors w-full";
    
    switch (status) {
      case 'none':
        return (
          <button
            onClick={() => handleFriendAction('send', user._id)}
            className={`${buttonBaseClasses} bg-primary-500 text-white hover:bg-primary-600`}
          >
            <UserPlus className="w-3 h-3" />
            <span>Add</span>
          </button>
        );
      case 'request_sent':
        return (
          <button
            className={`${buttonBaseClasses} bg-gray-400 text-white cursor-not-allowed`}
            disabled
          >
            <Clock className="w-3 h-3" />
            <span>Sent</span>
          </button>
        );
      case 'request_received':
        return (
          <div className="flex flex-col space-y-1 w-full">
            <button
              onClick={() => handleFriendAction('accept', user._id)}
              className={`${buttonBaseClasses} bg-green-500 text-white hover:bg-green-600`}
            >
              <Check className="w-3 h-3" />
              <span>Accept</span>
            </button>
            <button
              onClick={() => handleFriendAction('addBack', user._id)}
              className={`${buttonBaseClasses} bg-blue-500 text-white hover:bg-blue-600`}
            >
              <UserPlus className="w-3 h-3" />
              <span>Add Back</span>
            </button>
          </div>
        );
      case 'friends':
        return (
          <div className="flex flex-col space-y-1 w-full">
            <button
              onClick={() => handleFriendAction('chat', user._id)}
              className={`${buttonBaseClasses} bg-blue-500 text-white hover:bg-blue-600`}
            >
              <MessageSquare className="w-3 h-3" />
              <span>Chat</span>
            </button>
            <Link
              to={`/profile/${user._id}`}
              className={`${buttonBaseClasses} bg-green-500 text-white hover:bg-green-600`}
            >
              <User className="w-3 h-3" />
              <span>Profile</span>
            </Link>
            <button
              onClick={() => handleFriendAction('remove', user._id)}
              className={`${buttonBaseClasses} bg-red-500 text-white hover:bg-red-600`}
            >
              <UserMinus className="w-3 h-3" />
              <span>Remove</span>
            </button>
          </div>
        );
      case 'blocked':
        return (
          <button
            onClick={() => handleFriendAction('unblock', user._id)}
            className={`${buttonBaseClasses} bg-yellow-500 text-white hover:bg-yellow-600`}
          >
            <ShieldOff className="w-3 h-3" />
            <span>Unblock</span>
          </button>
        );
      default:
        return null;
    }
  };

  const UserCard = ({ user, showStats = true, showActions = true }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
            {user.profileImage ? (
              <img src={user.profileImage} alt={user.username} className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <span>{user.username.charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <Link 
              to={`/profile/${user._id}`}
              className="block hover:underline"
            >
              <h3 className="font-semibold text-gray-900 dark:text-white truncate hover:text-primary-600 dark:hover:text-primary-400">
                {user.firstName && user.lastName 
                  ? `${user.firstName} ${user.lastName}` 
                  : user.username
                }
              </h3>
            </Link>
            <p className="text-sm text-gray-500 dark:text-gray-400">@{user.username}</p>
            {user.bio && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">{user.bio}</p>
            )}
          </div>
        </div>
        
        {showActions && (
          <div className="flex flex-col items-end space-y-1 ml-3 min-w-[80px] flex-shrink-0">
            {getFriendshipButton(user, user.friendshipStatus)}
            {user.friendshipStatus !== 'blocked' && (
              <button
                onClick={() => handleFriendAction('block', user._id)}
                className="flex items-center justify-center space-x-1 px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors w-full"
              >
                <Shield className="w-3 h-3" />
                <span>Block</span>
              </button>
            )}
          </div>
        )}
      </div>

      {showStats && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <Zap className="w-4 h-4 text-yellow-500" />
                <span className="text-gray-600 dark:text-gray-300">{user.xp || 0} XP</span>
              </div>
              <div className="flex items-center space-x-1">
                <Crown className="w-4 h-4 crown-icon" />
                <span className="text-gray-600 dark:text-gray-300">{user.crowns || 0}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Trophy className="w-4 h-4 trophy-icon" />
                <span className="text-gray-600 dark:text-gray-300">{user.trophies || 0}</span>
              </div>
            </div>
            <div className="text-gray-500 dark:text-gray-400">
              Level {user.level || 1}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );

  const tabs = [
    { id: 'friends', name: 'Friends', icon: Users, count: friends.length },
    { id: 'requests', name: 'Requests', icon: UserCheck, count: friendRequests.received.length },
    { id: 'search', name: 'Search', icon: Search },
    { id: 'blocked', name: 'Blocked', icon: Shield, count: blockedUsers.length }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24 pb-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Friends</h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Connect with other debaters, view their achievements, and challenge them to debates
          </p>
        </motion.div>

        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg"
          >
            <div className="flex items-center justify-between">
              <span className="text-blue-800 dark:text-blue-200">{message}</span>
              <button
                onClick={() => setMessage('')}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Enhanced Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center mb-8"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-1 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex space-x-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isSelected = activeTab === tab.id;
                return (
                  <motion.button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center space-x-2 ${
                      isSelected
                        ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white shadow-lg transform scale-105'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
                    <span className="font-semibold">{tab.name}</span>
                    {tab.count !== undefined && tab.count > 0 && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        isSelected 
                          ? 'bg-white/20 text-white' 
                          : 'bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200'
                      }`}>
                        {tab.count}
                      </span>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-8"
        >
          {activeTab === 'friends' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <Users className="w-6 h-6 mr-2" />
                Your Friends ({friends.length})
              </h2>
              {friends.length === 0 ? (
                <div className="text-center py-16">
                  <Users className="w-20 h-20 text-gray-300 dark:text-gray-600 mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">No Friends Yet</h3>
                  <p className="text-lg text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                    Start connecting with other debaters to build your network!
                  </p>
                  <button
                    onClick={() => setActiveTab('search')}
                    className="btn-primary"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Find Friends
                  </button>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {friends.map((friend) => (
                    <UserCard key={friend._id} user={friend} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'requests' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <UserCheck className="w-6 h-6 mr-2" />
                Friend Requests ({friendRequests.received.length})
              </h2>
              {friendRequests.received.length === 0 ? (
                <div className="text-center py-16">
                  <UserCheck className="w-20 h-20 text-gray-300 dark:text-gray-600 mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">No Pending Requests</h3>
                  <p className="text-lg text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                    When someone sends you a friend request, it will appear here.
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {friendRequests.received.map((request) => (
                    <UserCard key={request._id} user={request} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'search' && (
            <div>
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search for users by username..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      handleSearch(e.target.value);
                    }}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {searchQuery && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                    <Search className="w-6 h-6 mr-2" />
                    Search Results ({searchResults.length})
                  </h2>
                  {searchResults.length === 0 ? (
                    <div className="text-center py-16">
                      <Search className="w-20 h-20 text-gray-300 dark:text-gray-600 mx-auto mb-6" />
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">No Users Found</h3>
                      <p className="text-lg text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                        Try searching with a different username.
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {searchResults.map((user) => (
                        <UserCard key={user._id} user={user} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'blocked' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <Shield className="w-6 h-6 mr-2" />
                Blocked Users ({blockedUsers.length})
              </h2>
              {blockedUsers.length === 0 ? (
                <div className="text-center py-16">
                  <Shield className="w-20 h-20 text-gray-300 dark:text-gray-600 mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">No Blocked Users</h3>
                  <p className="text-lg text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                    Users you block will appear here.
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {blockedUsers.map((user) => (
                    <UserCard key={user._id} user={user} />
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Friends; 